import { useState, useEffect, useRef } from "react"
import { Check, Link2, ArrowUpRight, ArrowRight } from "lucide-react"
import toast from "react-hot-toast"
import { daysAgoLabel, initials, parseSalary } from "./JobCard"

// Jobright-style sprite loaded from /public/jobright-icons.svg
const SPRITE = "/jobright-icons.svg"
function SpriteIcon({ id, size = 16, style }) {
  return (
    <svg width={size} height={size} aria-hidden="true" style={style}>
      <use href={`${SPRITE}#${id}`} />
    </svg>
  )
}

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"

function CompanyLogo({ logoUrl, companyName, size = 72 }) {
  const [bfErr, setBfErr] = useState(false)
  const [fbErr, setFbErr] = useState(false)
  const domain = companyName
    ? companyName.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null
  const src = !bfErr ? bfUrl : (!fbErr && logoUrl ? logoUrl : null)
  const onErr = () => { if (!bfErr) setBfErr(true); else setFbErr(true) }

  const base = {
    width: size, height: size, borderRadius: 8, flexShrink: 0,
    background: "transparent",
    border: "0.5px solid rgba(0,0,0,0.08)",
    objectFit: "contain",
    boxSizing: "border-box",
  }
  if (src) return <img src={src} alt={companyName} onError={onErr} loading="lazy" style={base} />
  return (
    <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontSize: size * 0.34, fontWeight: 700, color: "#18181b", fontFamily: FF }}>
        {initials(companyName)}
      </span>
    </div>
  )
}

// Deterministic "match" score derived from job id until the backend returns one
function pseudoScore(seed = "", floor = 72, ceil = 97) {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const span = ceil - floor
  return floor + (Math.abs(h) % (span + 1))
}

// Jobright-style ring: 70px, 3px stroke, conic gradient from the bottom (180deg)
function MatchRing({ score }) {
  const size = 70
  const stroke = 3
  // With `closest-side`, 100% of the gradient = half the element width (= radius).
  // So transparent inside the ring goes to (radius - stroke)/radius.
  const innerPct = ((size / 2 - stroke) / (size / 2)) * 100
  const grad = `conic-gradient(from 180deg at 50% 50%,
    rgb(0,240,160) 0%,
    rgb(23,186,255) ${score / 2}%,
    rgb(0,240,160) ${score}%,
    rgba(255,255,255,0.12) ${score}%,
    rgba(255,255,255,0.12) 100%)`
  const ringMask = `radial-gradient(circle closest-side, transparent ${innerPct}%, #000 ${innerPct + 0.5}%)`
  return (
    <div style={{
      position: "relative", width: size, height: size, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: grad,
        WebkitMask: ringMask, mask: ringMask,
      }} />
      <div style={{
        position: "relative",
        display: "flex", alignItems: "baseline",
        color: "#fff", fontFamily: FF,
      }}>
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 1, opacity: 0.85 }}>%</span>
      </div>
    </div>
  )
}

// Plain icon + text (no pill background) — matches Jobright's recommendation-tag
function ReasonRow({ label }) {
  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2, height: 16 }}>
      <Check size={12} strokeWidth={3} color="#00f0a0" style={{ flexShrink: 0 }} />
      <span style={{
        fontSize: 11, fontWeight: 500, lineHeight: "16px",
        color: "#fff", fontFamily: FF, maxWidth: 114,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{label}</span>
    </div>
  )
}

// Pastel chip like .index_publish-time / .index_jobTag / .index_company-highlight-tag
function Chip({ label, tint = "green" }) {
  const tints = {
    green:  "rgba(0, 240, 160, 0.1)",   // publish-time
    orange: "rgba(253, 167, 0, 0.1)",   // public company
    blue:   "rgba(20, 200, 240, 0.1)",  // early applicant / jobTag
    grey:   "rgba(0, 0, 0, 0.04)",      // social connection / fallback
  }
  return (
    <span style={{
      display: "inline-block",
      padding: "4px 6px", borderRadius: 6,
      background: tints[tint] || tints.grey,
      fontSize: 11, fontWeight: 500, lineHeight: "14px",
      color: "#000", fontFamily: FF, whiteSpace: "nowrap",
    }}>{label}</span>
  )
}

function MetaItem({ iconId, label }) {
  if (!label) return null
  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, minWidth: 0 }}>
      <SpriteIcon id={iconId} size={16} style={{ flexShrink: 0 }} />
      <span style={{
        fontSize: 13, fontWeight: 500, color: "#000", fontFamily: FF,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{label}</span>
    </div>
  )
}

export default function DesktopJobCard({
  job, onClick, selected,
  savedJobIds, onHeartToggle, onApply, onPass,
  isApplyStarting = false,
  isApplyStarted = false,
}) {
  const jobId = job?.id || job?.job_id || ""
  const [saved, setSaved] = useState(savedJobIds?.has(jobId) ?? false)
  const [hov, setHov] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => { setSaved(savedJobIds?.has(jobId) ?? false) }, [savedJobIds, jobId])

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false) }
    document.addEventListener("mousedown", onDoc)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDoc)
      document.removeEventListener("keydown", onKey)
    }
  }, [menuOpen])

  const company   = job?.company || "—"
  const title     = job?.role || "—"
  const location  = job?.canonical_location_id || job?.location || null
  const workType  = job?.work_type || "Full-time"
  const remoteLabel = job?.remote_type || (job?.is_remote ? "Remote" : "Onsite")
  const level = job?.normalization?.seniority_key && job.normalization.seniority_key !== "UNKNOWN"
    ? job.normalization.seniority_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : (job?.seniority_key && job.seniority_key !== "UNKNOWN"
       ? job.seniority_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
       : null)
  const expMin = job?.experience_min ?? job?.experienceMin
  const expLabel = expMin != null ? (expMin === 0 ? "Entry level" : `${expMin}+ years exp`) : null
  const posted = daysAgoLabel(job)
  const salary = (() => {
    const min = job?.salary_min, max = job?.salary_max
    if (min && max) return `$${Math.round(min / 1000)}K/yr - $${Math.round(max / 1000)}K/yr`
    return parseSalary(job?.salary_range) || null
  })()
  const sourceLabel = job?.source ? (job.source === "Custom" ? `${company} Careers` : job.source) : null

  const score = job?.match_score ?? job?.score ?? pseudoScore(jobId)
  const matchLabel = score >= 85 ? "STRONG MATCH" : score >= 70 ? "GOOD MATCH" : "FAIR MATCH"
  // Pseudo applicant count derived from job id (buckets of 5 for a believable feel)
  const applicantBucket = (() => {
    const buckets = [10, 25, 50, 100, 200]
    return buckets[pseudoScore(jobId, 0, buckets.length - 1)]
  })()
  const applicantLabel = `${applicantBucket}+ applicants`

  const handleSave = (e) => { e.stopPropagation(); setSaved(s => !s); onHeartToggle?.(job) }
  const handlePass = (e) => { e.stopPropagation(); onPass?.(job) }
  const handleApply = (e) => {
    e.stopPropagation()
    if (isApplyStarting || isApplyStarted) return
    onApply?.(job)
  }
  const handleVisitSite = (e) => {
    e.stopPropagation()
    const url = job?.job_url || job?.jobUrl
    if (!url) { toast.error("No job URL for this listing"); return }
    window.open(url, "_blank", "noopener,noreferrer")
  }
  const handleCopyUrl = async (e) => {
    e.stopPropagation()
    if (!jobId) return
    const base = typeof window !== "undefined" ? window.location.origin : ""
    try {
      await navigator.clipboard.writeText(`${base}/dashboard/jobs/${jobId}`)
      toast.success("Link copied")
    } catch {
      toast.error("Could not copy link")
    }
    setMenuOpen(false)
  }

  return (
    <div
      onClick={() => onClick?.(job)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        display: "block",
        background: hov ? "#f3f4f5" : "#ffffff",
        borderRadius: 16,
        boxShadow: "inset 0 0 0 1px #f0f1f2",
        padding: "16px 0 0 16px",
        cursor: "pointer",
        fontFamily: FF,
        transition: "background 0.15s ease",
      }}
    >
      {/* ── Left column: info + metadata + actions.
          Right padding = match block width (164) + 4px inset + 16px breathing room. */}
      <div style={{
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        paddingRight: 184, paddingBottom: 12,
      }}>
        {/* Top: logo + desc */}
        <div style={{ display: "flex", flexDirection: "row", gap: 16, marginBottom: 12 }}>
          <CompanyLogo logoUrl={job?.company_logo} companyName={company} size={72} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {/* Chip row */}
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4, marginBottom: 6 }}>
              {posted && <Chip label={posted} tint="green" />}
              {sourceLabel && <Chip label={sourceLabel} tint="orange" />}
              {expLabel && <Chip label={expLabel} tint="blue" />}
            </div>
            {/* Title */}
            <h3 style={{
              margin: "0 0 4px", fontSize: 20, fontWeight: 600,
              lineHeight: "24px", color: "#000", fontFamily: FF,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{title}</h3>
            {/* Company inline */}
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#000", fontFamily: FF }}>{company}</span>
              {job?.company_description && (
                <>
                  <span style={{ fontSize: 13, color: "#86909c" }}>/</span>
                  <span style={{
                    fontSize: 13, color: "#52525b", fontFamily: FF,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{job.company_description}</span>
                </>
              )}
            </div>
          </div>

          {/* More menu */}
          <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v) }}
              aria-label="More"
              style={{
                width: 32, height: 32, borderRadius: 9999,
                background: "#f3f4f5",
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#18181b",
                transition: "background 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#e5e6eb" }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f3f4f5" }}
            >
              <SpriteIcon id="more" size={18} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                onClick={e => e.stopPropagation()}
                style={{
                  position: "absolute", top: "calc(100% + 6px)", right: 0,
                  minWidth: 160, background: "#ffffff",
                  border: "1px solid #ececec", borderRadius: 10,
                  boxShadow: "0 10px 32px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)",
                  padding: 4, zIndex: 20, fontFamily: FF,
                }}
              >
                <button
                  role="menuitem" onClick={handleCopyUrl}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    width: "100%", padding: "8px 10px", borderRadius: 6,
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 12.5, fontWeight: 500, color: "#18181b",
                    fontFamily: FF, textAlign: "left",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f6f6f7"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <Link2 size={13} strokeWidth={2.2} />
                  Copy URL
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Middle: metadata — grid 3-col × 2 rows, Jobright spacing (8px rowGap, 10px vertical padding) */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          columnGap: 16, rowGap: 8,
          padding: "10px 0",
          borderTop: "0.5px solid rgba(0,0,0,0.08)",
          borderBottom: "0.5px solid rgba(0,0,0,0.08)",
        }}>
          <MetaItem iconId="location" label={location || "Not Defined"} />
          <MetaItem iconId="time"     label={workType} />
          <MetaItem iconId="money"    label={salary || "Not Defined"} />
          <MetaItem iconId="remote"   label={remoteLabel} />
          <MetaItem iconId="level"    label={level || "Not Defined"} />
          <MetaItem iconId="date"     label={expLabel || "Not Defined"} />
        </div>

        {/* Actions row */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: 10, gap: 12,
        }}>
          <span style={{
            fontSize: 12, fontWeight: 500, color: "#86909c", fontFamily: FF,
          }}>
            {applicantLabel}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={handlePass}
              aria-label="Pass"
              style={{
                width: 32, height: 32, borderRadius: 9999,
                background: "#f3f4f5", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#18181b",
                transition: "background 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#e5e6eb" }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f3f4f5" }}
            >
              <SpriteIcon id="dislike" size={16} />
            </button>
            <button
              onClick={handleSave}
              aria-label={saved ? "Saved" : "Save"}
              style={{
                width: 32, height: 32, borderRadius: 9999,
                background: "#f3f4f5", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: saved ? "#e11d48" : "#18181b",
                transition: "background 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#e5e6eb" }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f3f4f5" }}
            >
              {saved
                ? <svg width="16" height="16" viewBox="0 0 16 16" fill="#e11d48" aria-hidden="true"><path d="M0.716222 5.28447C0.103271 8.62748 2.60635 12.0025 6.98193 14.8718C7.59959 15.2769 8.3988 15.2768 9.01695 14.8725C13.3938 12.01 15.897 8.68157 15.2784 5.28447C14.5 1.00968 9.00001 0.268737 7.99921 3.5097C7.00001 0.268732 1.50001 1.00971 0.716222 5.28447Z" stroke="#e11d48" strokeWidth="1.16" strokeLinejoin="round"/></svg>
                : <SpriteIcon id="unlike" size={16} />
              }
            </button>
            <button
              onClick={handleVisitSite}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                gap: 6,
                height: 32, padding: "4px 14px",
                background: "#f3f4f5", color: "#18181b",
                border: "none", borderRadius: 32,
                fontSize: 13, fontWeight: 600, lineHeight: "16px",
                fontFamily: FF, cursor: "pointer",
                transition: "background 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#e5e6eb" }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f3f4f5" }}
            >
              Visit Site
              <ArrowUpRight size={14} strokeWidth={2.2} />
            </button>
            <button
              onClick={handleApply}
              disabled={isApplyStarting || isApplyStarted}
              aria-busy={isApplyStarting}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                gap: 6,
                height: 32, padding: "4px 14px",
                background: isApplyStarting
                  ? "#121212"
                  : isApplyStarted
                    ? "#f3f4f5"
                    : "#FD3752",
                color: isApplyStarted ? "#86909c" : "#ffffff",
                border: "none", borderRadius: 32,
                fontSize: 13, fontWeight: 700, lineHeight: "16px",
                fontFamily: FF,
                cursor: (isApplyStarting || isApplyStarted) ? "default" : "pointer",
                transition: "background 0.15s ease, color 0.15s ease",
                opacity: isApplyStarting ? 0.95 : 1,
                minWidth: isApplyStarting ? 128 : "auto",
              }}
              onMouseEnter={e => {
                if (!isApplyStarting && !isApplyStarted) e.currentTarget.style.background = "#e32945"
              }}
              onMouseLeave={e => {
                if (!isApplyStarting && !isApplyStarted) e.currentTarget.style.background = "#FD3752"
              }}
            >
              {isApplyStarting ? (
                <>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 12, height: 12, borderRadius: "50%",
                      border: "1.5px solid rgba(255,255,255,0.35)",
                      borderTopColor: "#ffffff",
                      animation: "spin 0.7s linear infinite",
                      display: "inline-block",
                    }}
                  />
                  Starting…
                </>
              ) : isApplyStarted ? (
                <>
                  <Check size={14} strokeWidth={2.6} />
                  Started
                </>
              ) : (
                <>
                  Agent Apply
                  <ArrowRight size={14} strokeWidth={2.2} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Right: match block (dark gradient, absolutely positioned 4px inset) ── */}
      <div style={{
        position: "absolute",
        top: 4, right: 4, bottom: 4,
        width: 164,
        padding: "24px 16px 16px",
        background: "linear-gradient(#000, #425250)",
        borderRadius: 12,
        color: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center",
      }}>
        {/* Score section (ring + match label) with bottom divider */}
        <div style={{
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          width: "100%", paddingBottom: 14,
          boxShadow: "inset 0 -1px 0 0 rgba(255,255,255,0.1)",
        }}>
          <MatchRing score={score} />
          <span style={{
            marginTop: 9,
            fontSize: 14, fontWeight: 600, lineHeight: "22px",
            color: "#fff", textAlign: "center", fontFamily: FF,
          }}>{matchLabel}</span>
        </div>

        {/* Reasons */}
        <div style={{
          alignSelf: "flex-start",
          display: "flex", flexDirection: "column", gap: 4,
          marginTop: 12, width: "100%",
        }}>
          {expLabel && <ReasonRow label="Experience match" />}
          {salary && <ReasonRow label="Comp. & Benefits" />}
        </div>
      </div>
    </div>
  )
}

// ── Skeleton: mirrors the real card layout (left info + dark match block) ──
export function DesktopJobCardSkeleton() {
  const bar = (w, h = 10) => (
    <div className="djc-skel" style={{ width: w, height: h, borderRadius: 4 }} />
  )
  return (
    <div
      style={{
        position: "relative",
        display: "block",
        background: "#ffffff",
        borderRadius: 16,
        boxShadow: "inset 0 0 0 1px #f0f1f2",
        padding: "16px 0 0 16px",
        fontFamily: FF,
      }}
    >
      <style>{`
        @keyframes djc-skel-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .djc-skel {
          background: linear-gradient(90deg, #f0f1f2 0%, #f7f8f9 50%, #f0f1f2 100%);
          background-size: 200% 100%;
          animation: djc-skel-shimmer 1.4s ease-in-out infinite;
        }
      `}</style>

      {/* Left column */}
      <div style={{
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        paddingRight: 184, paddingBottom: 12,
      }}>
        {/* Top: logo + title block */}
        <div style={{ display: "flex", flexDirection: "row", gap: 16, marginBottom: 12 }}>
          <div className="djc-skel" style={{ width: 72, height: 72, borderRadius: 8, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Chip row */}
            <div style={{ display: "flex", gap: 6 }}>
              {bar(70, 20)}
              {bar(90, 20)}
              {bar(60, 20)}
            </div>
            {/* Title */}
            {bar("65%", 20)}
            {/* Company */}
            {bar("30%", 13)}
          </div>
        </div>

        {/* Metadata — 2 rows of 3 */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          columnGap: 16, rowGap: 8,
          padding: "10px 0",
          borderTop: "0.5px solid rgba(0,0,0,0.08)",
          borderBottom: "0.5px solid rgba(0,0,0,0.08)",
        }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div className="djc-skel" style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0 }} />
              <div className="djc-skel" style={{ height: 10, borderRadius: 4, flex: 1 }} />
            </div>
          ))}
        </div>

        {/* Actions row */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: 10, gap: 12,
        }}>
          <div className="djc-skel" style={{ width: 80, height: 12, borderRadius: 4 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="djc-skel" style={{ width: 32, height: 32, borderRadius: 9999 }} />
            <div className="djc-skel" style={{ width: 32, height: 32, borderRadius: 9999 }} />
            <div className="djc-skel" style={{ width: 32, height: 32, borderRadius: 9999 }} />
            <div className="djc-skel" style={{ width: 94, height: 32, borderRadius: 9999 }} />
            <div className="djc-skel" style={{ width: 128, height: 32, borderRadius: 9999 }} />
          </div>
        </div>
      </div>

      {/* Match block (grey, not dark — this is loading state) */}
      <div style={{
        position: "absolute",
        top: 4, right: 4, bottom: 4,
        width: 164,
        padding: "24px 16px 16px",
        background: "#e8ebef",
        borderRadius: 12,
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "flex-start",
      }}>
        {/* Ring placeholder */}
        <div className="djc-skel" style={{ width: 70, height: 70, borderRadius: "50%" }} />
        {/* Match label */}
        <div className="djc-skel" style={{ marginTop: 12, width: 90, height: 14, borderRadius: 4 }} />
        {/* Divider */}
        <div style={{ width: "100%", height: 1, background: "rgba(0,0,0,0.06)", margin: "14px 0 12px" }} />
        {/* Reason rows */}
        <div style={{ alignSelf: "flex-start", display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
          {[70, 100].map((w, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div className="djc-skel" style={{ width: 12, height: 12, borderRadius: "50%", flexShrink: 0 }} />
              <div className="djc-skel" style={{ height: 10, borderRadius: 4, width: w + "%" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
